import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';

import { Attachment } from '../../models/attachments.model';
import { AttachmentsResponse } from '../../models/responseModels.ts/attachmentsResponse.model';

import { getDownloadURLByField } from '../../services/recruitsService';

import { useDeleteAttachmentsMutation } from '../../store/apiSlices/attachmentsApiSlice';

import { toastHandler } from '../../utils/toastHandler';
import { getFormattedDateString } from '../../functions/dateFunction';

import { StatusCode } from '../../enums/httpStatus.enum';
import uploadCloud from '../../assets/images/uploadCloud.svg';
import uploadDoc from '../../assets/icons/uploadDoc.svg';
import doc from '../../assets/icons/docIcon.svg';
import xlsDoc from '../../assets/icons/excelIcon.svg';
import pdfDoc from '../../assets/icons/pdfIcon.svg';
import closeIcon from '../../assets/icons/close.svg';
import mp3Icon from '../../assets/icons/mp3.svg';
import pptIcon from '../../assets/icons/ppt.svg';
import ImageAttactmentIcon from '../../assets/icons/imageAttachmentIcon.svg';
import zipIcon from '../../assets/icons/zip.svg';

const AttachmentComponent: React.FC<Attachment> = ({
	formik,
	isUploadable,
	attachmentsData,
	maxFiles,
	handleDelete,
}) => {
	const [searchParams] = useSearchParams();
	const recruit_id = searchParams.get('id');
	const [deleteAttachment, deleteAttachmentStatus] =
		useDeleteAttachmentsMutation();

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			acceptedFiles?.forEach((file: File) => {
				const reader = new FileReader();
				reader.onabort = () =>
					toastHandler({
						isError: true,
						msg: 'File Reading was aborted.',
					});
				reader.onerror = () =>
					toastHandler({
						isError: true,
						msg: 'File Reading was failed.',
					});
				reader.onload = () => {
					//
				};
				reader.readAsArrayBuffer(file);
			});
			if (acceptedFiles?.length) {
				formik.setFieldValue('files', [
					...formik.values.files,
					...acceptedFiles,
				]);
			}
		},
		[formik.values],
	);

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		accept: {
			'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
			'audio/*': ['.mp3', '.wav', '.aac'],
			'application/pdf': ['.pdf'],
			'application/msword': ['.doc', '.docx'],
			'application/vnd.ms-excel': ['.xls', '.xlsx'],
			'application/zip': ['.zip'],
			'application/vnd.ms-powerpoint': ['.pptx', '.ppt'],
		},
		multiple: true,
		maxFiles: maxFiles,
	});

	const getDocType = (fileName: string) => {
		const type = fileName.split('.').pop()?.toLocaleUpperCase();
		switch (type) {
			case 'DOC':
				return <img className="w-14 h-14" src={doc} alt="" />;
			case 'DOCX':
				return <img className="w-14 h-14" src={doc} alt="" />;
			case 'XLSX':
				return <img className="w-14 h-14" src={xlsDoc} alt="" />;
			case 'XLX':
				return <img className="w-14 h-14" src={xlsDoc} alt="" />;
			case 'PDF':
				return <img className="w-14 h-14" src={pdfDoc} alt="" />;
			case 'JPG':
				return (
					<img
						className="w-14 h-14"
						src={ImageAttactmentIcon}
						alt=""
					></img>
				);
			case 'JPEG':
				return (
					<img
						className="w-14 h-14"
						src={ImageAttactmentIcon}
						alt=""
					></img>
				);
			case 'PNG':
				return (
					<img
						className="w-14 h-14"
						src={ImageAttactmentIcon}
						alt=""
					></img>
				);
			case 'GIF':
				return (
					<img
						className="w-14 h-14"
						src={ImageAttactmentIcon}
						alt=""
					></img>
				);
			case 'SVG':
				return (
					<img
						className="w-14 h-14"
						src={ImageAttactmentIcon}
						alt=""
					></img>
				);
			case 'ZIP':
				return <img className="w-14 h-14" src={zipIcon} alt=""></img>;
			case 'PPT':
				return <img className="w-14 h-14" src={pptIcon} alt=""></img>;
			case 'PPTX':
				return <img className="w-14 h-14" src={pptIcon} alt=""></img>;
			case 'MP3':
				return <img className="w-14 h-14" src={mp3Icon} alt=""></img>;
			case 'WAV':
				return <img className="w-14 h-14" src={mp3Icon} alt=""></img>;
			case 'AAC':
				return <img className="w-14 h-14" src={mp3Icon} alt=""></img>;
		}
	};

	const onDelete = (file: File) => {
		const updatedFiles = formik.values.files?.filter((e: File) => {
			return e.name !== file.name;
		});
		formik.setFieldValue('files', [...updatedFiles]);
	};

	const handleDeleteAttachment = async (fileId: string) => {
		toastHandler({
			isLoading: true,
			msg: 'Deleting Attachment',
		});
		const res = await deleteAttachment(fileId);
		if (res.data?.statusCode == StatusCode.OK) {
			toastHandler({
				isError: false,
				msg: 'Attachment deleted successfully',
			});
			handleDelete?.();
		} else {
			toastHandler({
				isError: true,
				msg: res.data?.value as string,
			});
		}
	};

	const handleFile = async (fileId: string, fileName: string) => {
		if (!recruit_id) return;
		try {
			const downloadURL = await getDownloadURLByField({
				recruitId: recruit_id,
				fileId: fileId,
			});

			if (downloadURL?.value) {
				const a = document.createElement('a');
				a.href = downloadURL.value;
				a.download = fileName;
				a.click();
				a.remove();
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			toastHandler({
				isError: true,
				msg: error as string,
			});
		}
	};

	return (
		<div className="flex flex-col gap-y-3">
			{isUploadable ? (
				<div {...getRootProps()}>
					<div className="flex flex-col items-center border-4 text-center border-dashed py-4 gap-y-3 px-3 rounded-lg">
						<img src={uploadCloud} alt="" />
						<p>
							Drag and drop some files here, or click to select
							files
						</p>
						<button
							type="button"
							className="flex border-2 border-rcrm-lime text-rcrm-lime rounded-lg items-center gap-x-2 px-2"
						>
							<p>Choose File</p>
							<img src={uploadDoc} alt="" />
						</button>
					</div>
					<input {...getInputProps()} />
				</div>
			) : null}
			<div className="flex  flex-col gap-y-3">
				{isUploadable ? (
					<>
						{formik.values?.files?.map((file: File) => (
							<div
								className="flex justify-between items-center"
								key={file.lastModified}
							>
								<div className="flex gap-x-3 items-center">
									<img
										src={closeIcon}
										alt=""
										className="w-3 h-3 cursor-pointer"
										onClick={() => onDelete(file)}
									/>
									<div>{getDocType(file.name)}</div>
									<div className="font-semibold text-rcrm-primary">
										{file.name}
									</div>
								</div>
								{/* <div className="flex flex-col gap-y-1">
									<p>{getDate(file)}</p>
								</div> */}
							</div>
						))}
					</>
				) : (
					<>
						{attachmentsData?.map((file: AttachmentsResponse) => (
							<div
								className="flex justify-between items-center"
								key={file.item_id}
							>
								<div className="flex gap-x-3 items-center w-full">
									<div className="w-3 h-3">
										{file.is_editable ? (
											<img
												src={closeIcon}
												alt=""
												className="w-3 h-3 cursor-pointer"
												onClick={() =>
													handleDeleteAttachment(
														file.item_id,
													)
												}
											/>
										) : null}
									</div>
									<a
										onClick={() =>
											handleFile(
												file.item_id,
												file.file_name,
											)
										}
										className="flex items-center hover:underline cursor-pointer"
									>
										<div>{getDocType(file.file_name)}</div>
										<div
											title={file.file_name}
											className="relative font-semibold text-rcrm-primary max-w-[200px] overflow-hidden whitespace-nowrap text-ellipsis"
										>
											{file.file_name}
										</div>
									</a>
								</div>
								<div className="flex flex-col gap-y-1">
									<p>{file.created_by}</p>
									<p>
										{getFormattedDateString(
											file.created_date_time,
										)}
									</p>
								</div>
							</div>
						))}
					</>
				)}
			</div>
		</div>
	);
};

export default AttachmentComponent;
